import { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { fetchVideoBlob, fetchLastWatchedPosition, saveVideoProgress } from "../../../service/UserService/VideoService";
import { STREAM_VIDEO } from "../../../service/URLs";

const isYoutubeUrl = (url = "") =>
  url.includes("youtube.com") || url.includes("youtu.be");

const isDirectUrl = (url = "") =>
  url.startsWith("http://") || url.startsWith("https://");

export const useVideoPlayer = ({ isOpen, fileName, resourceType, contentId, courseId, isAcknowledged }) => {
  const playerRef = useRef(null);
  const lastPositionRef = useRef(0);
  const readyRef = useRef(false);
  const hasSeekedRef = useRef(false);
  const shouldRestartRef = useRef(false);
  const controlsTimeoutRef = useRef(null);
  const totalDurationRef = useRef(0);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [totalDuration, setTotalDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [lastWatchedPosition, setLastWatchedPosition] = useState(0);
  const [currentPosition, setCurrentPosition] = useState(0);
  const [lastPositionLoaded, setLastPositionLoaded] = useState(false);
  const [isEnded, setIsEnded] = useState(false);
  const [prevLastWatch, setPrevLastWatch] = useState(0);
  const [played, setPlayed] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [muted, setMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [playerReady, setPlayerReady] = useState(false);
  const storedUserId = localStorage.getItem("userId");
  const userId = useSelector((state) => state.auth.userId) || storedUserId;
  const userRole = useSelector((state) => state.auth?.roles?.[0]);
  const currentPortal = useSelector((state) => state.auth?.currentPortal);
  const isEmployee = (userRole === "EMPLOYEE") || (userRole==="ADMIN_SUPPORT" && currentPortal==="user");
  const contentIdRef = useRef(contentId);
  const roles = useSelector((state) => state.auth?.roles ?? []);
  const isAdminRole = currentPortal === "admin" && roles.some((r) => ["ADMIN", "ADMIN_SUPPORT"].includes(r));

useEffect(() => {
  if (contentId) { 
    contentIdRef.current = contentId;
  }
}, [contentId]);
const courseIdRef = useRef(courseId);

useEffect(() => {
  contentIdRef.current = contentId;
}, [contentId]);

useEffect(() => {
  courseIdRef.current = courseId;
}, [courseId]);
  const safeSeek = useCallback((seconds) => {
    let attempts = 0;
    const trySeek = () => {
      const inst = playerRef.current;
      if (inst && typeof inst.seekTo === "function") {
        inst.seekTo(seconds, "seconds");
        return;
      }
      if (attempts < 20) {
        attempts += 1;
        requestAnimationFrame(trySeek);
      }
    };
    trySeek();
  }, []);

  useEffect(() => {
    if (!isOpen || !fileName) return;
    if (isYoutubeUrl(fileName) || isDirectUrl(fileName) || resourceType === "youtube-link") {
      setVideoUrl(fileName);
      return;
    }

    setVideoUrl(STREAM_VIDEO(fileName));
    setVideoLoading(false);
  }, [isOpen, fileName, resourceType]);

  useEffect(() => {
    if (isOpen) {
      setPlayerReady(false); 
      lastPositionRef.current = 0;
      setTotalDuration(0);
      setIsPlaying(true);
      setIsEnded(false);
      setLastWatchedPosition(0);
      setCurrentPosition(0);
      readyRef.current = false;
      hasSeekedRef.current = false;
      setLastPositionLoaded(false);
      shouldRestartRef.current = false;
    } else {
      setVideoUrl(null);
      setIsPlaying(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !userId || !courseId || !contentId) return;
    const load = async () => {
      const saved = await fetchLastWatchedPosition(userId, courseId, contentId);
      setPrevLastWatch(saved);
      lastPositionRef.current = saved;
      setLastWatchedPosition(saved);
      setCurrentPosition(saved);
      setLastPositionLoaded(true);
    };
    load();
  }, [isOpen, userId, courseId, contentId]);

  useEffect(() => {
    if (!isOpen || !readyRef.current || !lastPositionLoaded || hasSeekedRef.current) return;
    hasSeekedRef.current = true;
    if (totalDuration > 0 && lastPositionRef.current >= totalDuration - 1) return;
    if (lastPositionRef.current > 0) safeSeek(lastPositionRef.current);
  }, [isOpen, lastPositionLoaded, safeSeek, totalDuration]);

  const handleMouseMove = useCallback(() => {
    setShowControls(true);
    clearTimeout(controlsTimeoutRef.current);
    controlsTimeoutRef.current = setTimeout(() => setShowControls(false), 3000);
  }, []);

  const updateProgress = useCallback((position) => {
    console.log({isAcknowledged})
    if (isAdminRole || isAcknowledged) return;
    const duration = totalDurationRef.current;
    if (!(position > 0 && duration > 0 && userId && courseId && contentId)) return;

    let pct = (position / duration) * 100;
    if (pct >= 95 || duration - position <= 5) pct = 100;
    const finalPos = position < prevLastWatch ? prevLastWatch : position;
    saveVideoProgress(userId, contentId, courseId, finalPos, pct, currentPortal);
  }, [userId, courseId, contentId, prevLastWatch]);

  const saveProgress = useCallback(async () => {
  if (isAdminRole || isAcknowledged) return;
  const pos = lastPositionRef.current;
  const duration = totalDurationRef.current;
  const currentContentId = contentIdRef.current;
  const currentCourseId = courseIdRef.current;

  if (!(pos > 0 && duration > 0)) return;
  if (!currentContentId || !currentCourseId) return;

  let pct = (pos / duration) * 100;
  if (pct >= 95 || duration - pos <= 5) pct = 100;
  const finalPos = pos < prevLastWatch ? prevLastWatch : pos;
  await saveVideoProgress(userId, currentContentId, currentCourseId, finalPos, pct, currentPortal);
}, [userId, prevLastWatch]);

  const handleReady = useCallback(() => {
    readyRef.current = true;
    setPlayerReady(true);
    if (lastPositionLoaded && !hasSeekedRef.current) {
      hasSeekedRef.current = true;
      if (lastPositionRef.current > 0) safeSeek(lastPositionRef.current);
    }
  }, [lastPositionLoaded, safeSeek]);

  const handleProgress = useCallback((progress) => {
    const currentTime = progress.playedSeconds || 0;
    setPlayed(progress.played || 0);
    setCurrentPosition(currentTime);
    lastPositionRef.current = currentTime;
    if (isEmployee) {
      if (currentTime > lastWatchedPosition + 2) {
        playerRef.current?.seekTo(lastWatchedPosition, "seconds");
        return;
      }
      if (currentTime > lastWatchedPosition) {
        setLastWatchedPosition(currentTime);
      }
    } else {
      setLastWatchedPosition(currentTime);
    }
  }, [isEmployee, lastWatchedPosition]);

  const handleDuration = useCallback((duration) => {
    const d = duration || 0;
    setTotalDuration(d);
    totalDurationRef.current = d;
    if (d > 0 && lastPositionRef.current >= d - 1) {
      shouldRestartRef.current = true;
      lastPositionRef.current = 0;
      setCurrentPosition(0);
      setLastWatchedPosition(0);
      playerRef.current?.seekTo(0, "seconds");
    }
  }, []);

  const handleVideoEnd = useCallback(() => {
    const duration = totalDurationRef.current;
    if (duration === 0) return;

    setIsPlaying(false);
    setIsEnded(true);
    lastPositionRef.current = duration;
    updateProgress(duration);
    setLastWatchedPosition(duration);
  }, [updateProgress]);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
    const pos = lastPositionRef.current;
    if (pos > 0 && totalDuration > 0) updateProgress(pos);
  }, [totalDuration, updateProgress]);

  const handlePlay = useCallback(() => {
    setIsPlaying(true);
    setIsEnded(false);
    if (shouldRestartRef.current) {
      shouldRestartRef.current = false;
      safeSeek(0);
    }
  }, [safeSeek]);

  const handleSeekChange = useCallback((val) => {
    setPlayed(val);
    playerRef.current?.seekTo(val, "fraction");
  }, []);

  const togglePlay = useCallback(() => setIsPlaying((p) => !p), []);
  const toggleMute = useCallback(() => setMuted((m) => !m), []);

  const handleVolumeChange = useCallback((val) => {
    setVolume(val);
    setMuted(false);
  }, []);

  const handleFullscreen = useCallback(() => {
    const el = document.querySelector(".video-wrapper");
    if (document.fullscreenElement) document.exitFullscreen();
    else el?.requestFullscreen();
  }, []);

  return {
    playerRef,
    videoUrl,
    videoLoading,
    totalDuration,
    isPlaying,
    currentPosition,
    lastWatchedPosition,
    played,
    volume,
    muted,
    isEnded,
    setIsEnded,
    showControls,
    handleReady,
    playerReady,
    handleProgress,
    handleDuration,
    handleVideoEnd,
    handlePause,
    handlePlay,
    handleSeekChange,
    handleMouseMove,
    togglePlay,
    toggleMute,
    handleVolumeChange,
    handleFullscreen,
    saveProgress,
  };
};